import { z } from "zod";
import type { StayPlanContractDraft } from "../plans/stayPlanContractDraft";

export const recurrenceSchema = z.object({
  frequency: z.enum(["weekly", "biweekly", "monthly"]),
  interval: z.number().int().min(1),
  weekdays: z.array(z.number().int().min(0).max(6)),
  monthday: z.number().int().min(1).max(31).nullable(),
  endsOn: z.string().min(1),
});

export type RecurrenceFrequencyOption = "weekly" | "biweekly" | "monthly";

export const comandaLineSchema = z.object({
  itemId: z.string().nullable(),
  itemName: z.string().min(1),
  quantity: z.number().min(0.01),
  unitPrice: z.number().min(0),
  billingRole: z.enum(["stay", "extra"]).default("extra"),
});

export type ComandaLine = z.infer<typeof comandaLineSchema>;

export const reservationSchema = z.object({
  moduleType: z.enum(["day_care", "hotel"]),
  isOvernight: z.boolean().default(false),
  petId: z.string().min(1),
  ownerId: z.string().optional(),
  checkInDate: z.string().min(1),
  checkOutDate: z.string().nullable().optional(),
  checkInTime: z.string().nullable().optional(),
  checkOutTime: z.string().nullable().optional(),
  stayPackIds: z.array(z.string()).default([]),
  hotelRoomId: z.string().nullable().optional(),
  hotelRoomBedId: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  recurrence: recurrenceSchema.nullable().optional(),
  dateMode: z.enum(["single", "pick", "repeat"]).default("single"),
  /** Turno de chegada e de saída; só no modo "manhã e noite" da clínica. */
  arrivalShift: z.enum(["morning", "evening"]).nullable().default(null),
  departureShift: z.enum(["morning", "evening"]).nullable().default(null),
  /** Só hotel avulso: quando cobrar. NULL segue o padrão da clínica. */
  billingTiming: z.enum(["check_in", "check_out"]).nullable().default(null),
  pickedDates: z.array(z.string()).default([]),
  excludedDates: z.array(z.string()).default([]),
  /** Plano semanal a contratar junto com a reserva. Só é gravado no submit. */
  planDraft: z.custom<StayPlanContractDraft | null>((value) => value === null || typeof value === "object").default(null),
  billing: z.enum(["plan", "avulso"]).default("avulso"),
  planChoice: z.enum(["existing", "avulso"]).nullable().default(null),
  stayPlanContractId: z.string().nullable().default(null),
  stayPlanUnitsToUse: z.number().int().positive().nullable().default(null),
  stayPlanName: z.string().nullable().default(null),
  stayPlanChargeInComanda: z.boolean().default(false),
  waivedStayCharges: z.object({
    plan: z.boolean().optional(),
    daily: z.boolean().optional(),
  }).default({}),
  comanda: z.array(comandaLineSchema).default([]),
}).refine((value) => value.moduleType !== "hotel" || !value.checkOutDate || value.checkOutDate >= value.checkInDate, {
  path: ["checkOutDate"],
}).refine((value) => value.moduleType !== "hotel" || Boolean(value.checkOutDate && value.checkOutDate > value.checkInDate), {
  path: ["checkOutDate"],
  message: "validation.hotelNightsRequired",
}).refine(
  (value) => {
    if (!value.recurrence) return true;
    const limit = new Date(value.checkInDate);
    limit.setMonth(limit.getMonth() + 12);
    return value.recurrence.endsOn <= limit.toISOString().slice(0, 10);
  },
  { path: ["recurrence", "endsOn"], message: "validation.recurrenceEndsOnTooFar" },
).superRefine((value, context) => {
  if (value.moduleType === "hotel" && !value.hotelRoomId && !value.hotelRoomBedId) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["hotelRoomId"],
      message: "validation.hotelAccommodationRequired",
    });
  }

  if (value.planChoice === "existing" && !value.stayPlanContractId) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["stayPlanContractId"],
      message: "validation.petPlanRequired",
    });
  }
});

export type ReservationFormValues = z.infer<typeof reservationSchema>;
